import { type FC } from 'react';

import { AvailabilityBadge } from '../AvailabilityBadge';
import { type AvailabilityBadgeVariant } from '../AvailabilityBadge/AvailabilityBadge.types';
import { Box } from '../Box';
import { Text } from '../Typography';
import { UserCard } from '../UserCard';
import ReviewImages from './components/ReviewImages';
import ReviewReply, { type ReviewReplyProps } from './components/ReviewReply';

export type ReviewCardProps = {
  avatarSrc: string;
  name: string;
  date: string;
  rating: number;
  availabilityBadge?: {
    variant: AvailabilityBadgeVariant;
    text: string;
  };
  content: string;
  images?: string[];
  replies?: ReviewReplyProps[];
  className?: string;
};

const ReviewCard: FC<ReviewCardProps> = ({
  avatarSrc,
  name,
  date,
  rating,
  availabilityBadge,
  content,
  images = [],
  replies = [],
  className,
}) => {
  return (
    <Box
      backgroundColor="white"
      borderRadius="var(--spacing-4) 0"
      p="var(--spacing-4) 0"
      display="flex"
      flexDirection="column"
      gap="var(--spacing-4)"
      className={className}
    >
      {/* Header with avatar, name, date, and stars */}
      <UserCard
        avatarSrc={avatarSrc}
        title={name}
        subtitle={date}
        rating={rating}
      />

      {/* Availability badge */}
      {availabilityBadge && (
        <Box>
          <AvailabilityBadge variant={availabilityBadge.variant}>
            {availabilityBadge.text}
          </AvailabilityBadge>
        </Box>
      )}

      {/* Review content */}
      <Text size="md">{content}</Text>

      {/* Images */}
      {images.length > 0 && <ReviewImages images={images} />}

      {/* Replies */}
      {replies.length > 0 && (
        <Box display="flex" flexDirection="column" gap="var(--spacing-3)">
          {replies.map((reply, index) => (
            <ReviewReply
              key={index}
              avatarSrc={reply.avatarSrc}
              name={reply.name}
              date={reply.date}
              content={reply.content}
              label={reply.label}
              rating={reply.rating}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ReviewCard;
